import React, { useEffect, useState } from 'react';
import { CardSurface, TagGeneral, TooltipTrigger, Spinner, Text, SparkIcon } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { getTaskTypeShortLabel } from '@/features/tasks/utils/types';

const PRIMARY_METRIC_BY_TASK = {
    [TaskType.classification]: { key: 'f1_score', label: 'F1' },
    [TaskType.NER]: { key: 'f1_score', label: 'F1' },
    [TaskType.NER_WITH_RELATIONS]: { key: 'f1_score', label: 'F1' },
    [TaskType.summarization]: { key: 'semantic_similarity', label: 'Semantic Similarity' },
    [TaskType.contentGeneration]: { key: 'semantic_similarity', label: 'Semantic Similarity' },
};

const formatMetricValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    if (typeof value !== 'number') return String(value);
    if (value <= 1) return `${(value * 100).toFixed(1)}%`;
    return value.toFixed(1);
};

const TaskEvaluationOverview = ({ subGroup, activeUsecaseId, onSelect }) => {
    const usecases = subGroup?.usecases || [];
    const [metricsById, setMetricsById] = useState({});
    const [loadingById, setLoadingById] = useState({});

    useEffect(() => {
        let cancelled = false;
        usecases.forEach((uc) => {
            if (metricsById[uc._id] !== undefined) return;
            setLoadingById((s) => ({ ...s, [uc._id]: true }));
            data_from_server_post(
                'evaluation',
                {
                    client_id: uc.client_id,
                    llm_usecase_id: uc._id,
                    version: uc.version,
                    comparison_mode: false,
                },
                (result) => {
                    if (cancelled) return;
                    setLoadingById((s) => ({ ...s, [uc._id]: false }));
                    setMetricsById((s) => ({ ...s, [uc._id]: result || null }));
                }
            );
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usecases.map((u) => u._id).join('|')]);

    if (usecases.length < 2) return null;

    const aggregateLatencyMs = usecases.reduce((acc, uc) => {
        const r = metricsById[uc._id];
        return acc + (r?.latency_ms || r?.avg_latency_ms || 0);
    }, 0);

    return (
        <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                    <Text variant="heading-5-semibold" color="primary" as="div">
                        Task Overview
                    </Text>
                    <Text variant="detail-regular" color="secondary" as="div">
                        Per-objective metrics for{' '}
                        <Text variant="detail-semibold" as="span">{subGroup.sub_group_name}</Text>. Click an objective for details.
                    </Text>
                </div>
                {aggregateLatencyMs > 0 && (
                    <TooltipTrigger text="Combined median latency across all objectives in a single task call.">
                        <span>
                            <TagGeneral label={`~${aggregateLatencyMs.toFixed(0)} ms / call`} />
                        </span>
                    </TooltipTrigger>
                )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {usecases.map((uc) => {
                    const isActive = uc._id === activeUsecaseId;
                    const isLoading = loadingById[uc._id];
                    const metricsResult = metricsById[uc._id];
                    const metricCfg = PRIMARY_METRIC_BY_TASK[uc.task_type] || { key: null, label: 'Score' };
                    const value = metricsResult?.[metricCfg.key];
                    return (
                        <CardSurface
                            key={uc._id}
                            background="white"
                            padding="medium"
                            className={isActive ? 'spark-card-active' : ''}
                        >
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelect?.(uc._id)}
                                onKeyDown={(e) => { if (e.key === 'Enter') onSelect?.(uc._id); }}
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: 8,
                                    border: isActive ? '2px solid var(--Semantic-Content-brand, #1D4ED8)' : '1px solid var(--Semantic-Border-default, #E5E7EB)',
                                    background: isActive ? 'var(--Semantic-Bg-brand-light, #EFF6FF)' : '#fff',
                                    padding: 16,
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                    <TagGeneral label={getTaskTypeShortLabel(uc.task_type)} />
                                    <SparkIcon slug="chevron-right" size="small" />
                                </div>
                                <Text variant="base-semibold" color="primary" as="div" style={{ marginBottom: 8 }}>
                                    {uc.usecase_name}
                                </Text>
                                <Text variant="detail-regular" color="secondary" as="div" style={{ marginBottom: 4 }}>
                                    {metricCfg.label}
                                </Text>
                                <Text variant="heading-3-semibold" as="div" style={{ color: 'var(--Semantic-Content-brand, #1D4ED8)' }}>
                                    {isLoading ? <Spinner size="small" /> : formatMetricValue(value)}
                                </Text>
                            </div>
                        </CardSurface>
                    );
                })}
            </div>
        </div>
    );
};

export default TaskEvaluationOverview;
